import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Models } from "appwrite";
import { account } from "../lib/appwrite";

type AuthValue = {
  user: Models.User<Models.Preferences> | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Models.User<Models.Preferences> | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try { setUser(await account.get()); }
    catch { setUser(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { void refresh(); }, []);

  const value = useMemo(() => ({
    user,
    loading,
    refresh,
    logout: async () => { await account.deleteSession("current"); setUser(null); },
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be inside AuthProvider");
  return value;
}
